import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useDomainServices } from "@/providers/DomainServices";
import { useNavigation } from "@/providers/navigation/NavigationContext";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { usePublishToAiAgentThreadPubSubBus } from "@/shared/services/AiAgentThreadPubSubBus";
import {
  WORKSPACE_ACTION_TOPIC,
  type WorkspaceActionPayload,
} from "@/shared/services/AiAgentThreadPubSubBus/models";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import {
  AgentThreadRecord,
  AgentThreadRole,
  AgentThreadStatus,
  AgentThreadToolUseContent,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
} from "../AgentThreadRecord";
import { getClientTool } from "../clientTools";
import type { ApprovalMode } from "../clientTools/approvalModes";
import {
  ClientToolBatchRunner,
  type PendingToolApproval,
} from "../clientTools/ClientToolBatchRunner";
import type { ClientToolDeps } from "../clientTools/ClientTools";

export type { PendingToolApproval };

/**
 * Returns the client-tool `tool_use` blocks that still need execution in a
 * thread that is in CLIENT_TOOL_TURN.
 *
 * Finds the latest assistant message (scanning from the end — NOT just
 * `messages.at(-1)`, since the server may append a `roboto` message
 * carrying partial results for server-side tool uses that ran in the same
 * turn before handing control back to the browser), collects its tool_uses,
 * and filters out any whose `tool_use_id` already has a matching
 * `tool_result` in a subsequent `roboto` message. The remaining tool_uses
 * are the ones the client still owes results for.
 *
 * Also filters out tool_uses whose `tool_name` is not a known client tool.
 * In the mixed server+client turn the session can flip to CLIENT_TOOL_TURN
 * before the backend has finished persisting every server tool_result, so
 * an unanswered server tool_use briefly looks "pending" from the browser's
 * view of the delta. Submitting a client-side error result for it would
 * land a second tool_result for the same id once the backend's real
 * tool_result arrives, and Bedrock rejects the next Converse call
 * ("The number of toolResult blocks … exceeds the number of toolUse
 * blocks"). Leave those for the backend to answer.
 *
 * Exported for unit tests.
 */
export function findPendingClientToolUses(
  agentThread: AgentThreadRecord,
  isClientTool: (toolName: string) => boolean = (name) =>
    getClientTool(name) !== undefined,
): AgentThreadToolUseContent[] {
  let assistantIdx = -1;
  for (let i = agentThread.messages.length - 1; i >= 0; i -= 1) {
    if (agentThread.messages[i].role === AgentThreadRole.ASSISTANT) {
      assistantIdx = i;
      break;
    }
  }
  if (assistantIdx === -1) {
    return [];
  }

  const toolUses = agentThread.messages[assistantIdx].content.filter(
    isAgentThreadToolUseContent,
  );
  if (toolUses.length === 0) {
    return [];
  }

  const resolvedIds = new Set<string>();
  for (let i = assistantIdx + 1; i < agentThread.messages.length; i += 1) {
    const msg = agentThread.messages[i];
    if (msg.role !== AgentThreadRole.ROBOTO) {
      continue;
    }
    for (const block of msg.content) {
      if (isAgentThreadToolResultContent(block)) {
        resolvedIds.add(block.tool_use_id);
      }
    }
  }

  return toolUses.filter(
    (tu) => !resolvedIds.has(tu.tool_use_id) && isClientTool(tu.tool_name),
  );
}

/**
 * Hook that watches a AgentThreadRecord and handles client-side tool execution,
 * including approval gating based on per-tool approval modes.
 *
 * Delegates batch logic (triage, queue, result accumulation) to
 * {@link ClientToolBatchRunner}. This hook is a thin adapter that bridges
 * React context to the runner's lifecycle methods.
 */
export function useClientToolExecution(
  agentThread: AgentThreadRecord | undefined,
  orgId: string | undefined,
  autoEnabled: boolean,
  userPreferences: Record<string, ApprovalMode> | null | undefined,
) {
  const {
    agentThreadService,
    events: eventService,
    topics: topicService,
    files: fileService,
  } = useDomainServices();
  const { goto } = useNavigation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const publishWorkspaceAction =
    usePublishToAiAgentThreadPubSubBus<WorkspaceActionPayload>(
      WORKSPACE_ACTION_TOPIC,
    );

  const lastExecutedRef = useRef<string | null>(null);
  const [pendingApproval, setPendingApproval] =
    useState<PendingToolApproval | null>(null);

  // Deps are rebuilt each render and stored in a ref so the runner always reads
  // current values (e.g. isOnVisualizerPage reflects the user's current route).
  // The single `deps` object feeds both the ref's initial value and `current`,
  // so the closures (isOnVisualizerPage, onEventCreated) have one definition
  // rather than two copies that can drift apart.
  const deps: ClientToolDeps = {
    goto,
    orgId,
    eventService,
    topicService,
    fileService,
    isOnVisualizerPage: () => location.pathname.startsWith("/visualize"),
    publishWorkspaceAction,
    onEventCreated: () => {
      if (!orgId) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(orgId),
      });
    },
  };
  const depsRef = useRef(deps);
  depsRef.current = deps;

  // Stable refs for callbacks so the runner can be created once.
  const agentThreadServiceRef = useRef(agentThreadService);
  agentThreadServiceRef.current = agentThreadService;
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;

  // Create runner once with stable callbacks
  const runnerRef = useRef<ClientToolBatchRunner | null>(null);
  if (!runnerRef.current) {
    runnerRef.current = new ClientToolBatchRunner({
      onPendingApprovalChanged: (pending) => setPendingApproval(pending),
      onBatchComplete: (sessionId, results) => {
        const currentOrgId = orgIdRef.current;
        if (!currentOrgId || results.length === 0) {
          return;
        }
        agentThreadServiceRef.current
          .submitToolResults(
            sessionId,
            { tool_results: results },
            { orgId: currentOrgId },
          )
          .catch((error: unknown) => {
            // Do NOT clear lastExecutedRef — tool side effects (navigation,
            // PubSub) are not idempotent and must not be re-executed.
            ErrorMonitoringService.captureError(error as Error);
          });
      },
    });
  }

  // Main effect: detect CLIENT_TOOL_TURN and delegate to runner
  useEffect(() => {
    if (!agentThread || !orgId) {
      return;
    }
    if (agentThread.status !== AgentThreadStatus.CLIENT_TOOL_TURN) {
      return;
    }

    const pending = findPendingClientToolUses(agentThread);
    if (pending.length === 0) {
      return;
    }

    // Deduplicate — prevent re-executing the same batch on re-renders
    const executionKey = `${agentThread.thread_id}:${agentThread.messages.length}`;
    if (lastExecutedRef.current === executionKey) {
      return;
    }
    lastExecutedRef.current = executionKey;

    void runnerRef.current!.startBatch({
      sessionId: agentThread.thread_id,
      toolUses: pending,
      autoEnabled,
      userPreferences,
      deps: depsRef.current,
      resolveClientTool: getClientTool,
    });
  }, [agentThread, orgId, autoEnabled, userPreferences]);

  const handleApprove = useCallback(async () => {
    await runnerRef.current?.approve(depsRef.current);
  }, []);

  const handleDecline = useCallback(() => {
    runnerRef.current?.decline();
  }, []);

  return {
    pendingApproval,
    handleApprove,
    handleDecline,
  };
}
